import { useEffect, useMemo, useState } from 'react'
import {
  abonnementCourant,
  abonnementEnVigueur,
  changementProgramme,
  consequenceStatut,
  controlerCreneaux,
  controlerFiche,
  coursesDuMois,
  descriptionConditions,
  estSurDevis,
  historiqueAbonnements,
  libelleDuPlan,
  libelleForfait,
  LIBELLE_STATUT_CLIENT,
  PASTILLE_STATUT_CLIENT,
  type FicheClient,
} from '../../api/abonnement'
import { ADRESSE_VIDE, aUnPoint, intituleAdresse, LIBELLE_SOURCE, libelleAdresse, type AdresseConnue, type AdresseSaisie } from '../../api/adresses'
import { jourBamako } from '../../api/encaissement'
import {
  abonnerClient,
  adressesCompletesDuClient,
  changerForfaitClient,
  enregistrerCreneaux,
  modifierClient,
  supprimerAdresse,
} from '../../api/operations'
import type { Client, Creneau, DonneesOps, StatutClient } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, fcfa, JOURS, QUARTIERS } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante, type OptionListe } from '../ListeDeroulante'
import { PanneauLateral } from '../PanneauLateral'
import { SelecteurAdresse } from '../SelecteurAdresse'

const OPTIONS_JOUR: OptionListe[] = JOURS.slice(1).map((j, i) => ({ valeur: String(i + 1), libelle: j }))
const OPTIONS_JOUR_FACTURATION: OptionListe[] = Array.from({ length: 28 }, (_, i) => ({ valeur: String(i + 1), libelle: `Le ${i + 1} du mois suivant` }))
const CRENEAU_PAR_DEFAUT: Creneau = { weekday: 1, start_time: '09:00', end_time: '11:00' }

const entier = (s: string): number => {
  const t = s.replace(/[\s  ]/g, '')
  return /^\d+$/.test(t) ? Number(t) : Number.NaN
}

/**
 * La fiche d'un client, en panneau latéral : identité, collecte habituelle (épingle), option longue distance,
 * supplément hors créneau, créneaux (ajout, modification, suppression), forfait (souscrire, changer — effectif
 * le 1er du mois suivant), statut (pause, clôture, réactivation) et adresses mémorisées.
 *
 * Qui peut quoi (la base reste juge) : propriétaire et dispatch modifient la fiche ; le comptable la LIT.
 * Souscrire / changer de forfait : propriétaire, comptable et dispatch — ce dernier seulement sur un forfait au
 * prix de la grille (jamais un prix négocié).
 */
export function PanneauClient({
  client,
  donnees,
  orgId,
  surFermer,
  surRafraichir,
  surNotifier,
}: {
  client: Client
  donnees: DonneesOps
  orgId: string
  surFermer: () => void
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const peutEcrire = droits.peut('clients') && !droits.apercu
  const pme = client.type === 'pme'
  const lectureSeule = !peutEcrire

  return (
    <PanneauLateral
      titre={client.name}
      sousTitre={
        <>
          {pme ? 'PME' : 'Particulier'} · <span className={`badge ${PASTILLE_STATUT_CLIENT[client.status]} plain`}>{LIBELLE_STATUT_CLIENT[client.status]}</span>
          {lectureSeule && ` · lecture seule — ${droits.apercu ? 'aperçu de développement' : droits.refus}`}
        </>
      }
      surFermer={surFermer}
    >
      <SectionFiche client={client} orgId={orgId} lectureSeule={lectureSeule} surRafraichir={surRafraichir} surNotifier={surNotifier} />
      {pme && <SectionCreneaux client={client} orgId={orgId} lectureSeule={lectureSeule} surRafraichir={surRafraichir} surNotifier={surNotifier} />}
      {pme && <SectionForfait client={client} donnees={donnees} surRafraichir={surRafraichir} surNotifier={surNotifier} />}
      <SectionStatut client={client} donnees={donnees} lectureSeule={lectureSeule} surRafraichir={surRafraichir} surNotifier={surNotifier} />
      <SectionAdresses client={client} orgId={orgId} lectureSeule={lectureSeule} surNotifier={surNotifier} />
    </PanneauLateral>
  )
}

/* ------------------------------------------------------------------ fiche */

function SectionFiche({
  client,
  lectureSeule,
  surRafraichir,
  surNotifier,
}: {
  client: Client
  orgId: string
  lectureSeule: boolean
  surRafraichir: () => Promise<void>
  surNotifier: (m: string) => void
}) {
  const pme = client.type === 'pme'
  const [fiche, setFiche] = useState<FicheClient>({
    name: client.name,
    contact_name: client.contact_name ?? '',
    phone: client.phone ?? '',
    email: client.email ?? '',
    quartier: client.quartier ?? '',
    commune: client.commune ?? '',
    notes: client.notes ?? '',
    offslot_surcharge_fcfa: String(client.offslot_surcharge_fcfa),
  })
  const [optionLD, setOptionLD] = useState(client.option_longue_distance)
  const [collecte, setCollecte] = useState<AdresseSaisie>({
    ...ADRESSE_VIDE,
    texte: client.default_pickup,
    lat: client.default_pickup_lat,
    lng: client.default_pickup_lng,
  })
  const [collecteModifiee, setCollecteModifiee] = useState(false)
  const [enCours, setEnCours] = useState(false)
  const maj = (patch: Partial<FicheClient>) => setFiche((f) => ({ ...f, ...patch }))

  async function enregistrer() {
    const motifs = controlerFiche(fiche, client.type)
    if (motifs.length) return surNotifier(motifs.join(' '))
    setEnCours(true)
    try {
      await modifierClient(client.id, {
        name: fiche.name.trim(),
        contact_name: fiche.contact_name.trim() || null,
        phone: fiche.phone.trim(),
        email: fiche.email.trim() || null,
        quartier: fiche.quartier.trim() || null,
        commune: fiche.commune.trim() || null,
        notes: fiche.notes.trim() || null,
        ...(pme ? { offslot_surcharge_fcfa: entier(fiche.offslot_surcharge_fcfa), option_longue_distance: optionLD } : {}),
        ...(collecteModifiee
          ? {
              default_pickup_address: libelleAdresse(collecte) || null,
              default_pickup_lat: aUnPoint(collecte) ? collecte.lat : null,
              default_pickup_lng: aUnPoint(collecte) ? collecte.lng : null,
            }
          : {}),
      })
      surNotifier(`Fiche enregistrée : ${fiche.name.trim()}`)
      setCollecteModifiee(false)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  const champ = (id: string, etiquette: string, valeur: string, surChange: (v: string) => void, extra?: { placeholder?: string; type?: string }) => (
    <div className="field">
      <label htmlFor={id}>{etiquette}</label>
      <input id={id} value={valeur} disabled={lectureSeule} type={extra?.type ?? 'text'} placeholder={extra?.placeholder} onChange={(e) => surChange(e.target.value)} autoComplete="off" />
    </div>
  )

  return (
    <div className="panneau-section">
      <h4>
        <Icone nom="user" classe="h-ic" /> Fiche
      </h4>
      {champ('pc-nom', pme ? 'Nom du client *' : 'Nom *', fiche.name, (v) => maj({ name: v }))}
      {pme && champ('pc-contact', 'Interlocuteur', fiche.contact_name, (v) => maj({ contact_name: v }))}
      <div className="grille-2">
        {champ('pc-tel', 'Téléphone *', fiche.phone, (v) => maj({ phone: v }), { placeholder: '+223 …' })}
        {champ('pc-mail', 'E-mail', fiche.email, (v) => maj({ email: v }), { type: 'email' })}
      </div>
      <div className="grille-2">
        <div className="field">
          <label>Quartier</label>
          <ListeDeroulante
            valeur={fiche.quartier}
            options={[{ valeur: '', libelle: '—' }, ...(fiche.quartier && !(QUARTIERS as readonly string[]).includes(fiche.quartier) ? [{ valeur: fiche.quartier, libelle: fiche.quartier }] : []), ...QUARTIERS.map((q) => ({ valeur: q, libelle: q }))]}
            surChangement={(v) => maj({ quartier: v })}
            pleineLargeur
            {...(lectureSeule ? { desactive: true } : {})}
          />
        </div>
        {champ('pc-commune', 'Commune', fiche.commune, (v) => maj({ commune: v }), { placeholder: 'ex. Commune IV' })}
      </div>
      <div className="field">
        <label htmlFor="pc-notes">Notes</label>
        <textarea id="pc-notes" rows={3} value={fiche.notes} disabled={lectureSeule} onChange={(e) => maj({ notes: e.target.value })} style={{ width: '100%' }} />
      </div>

      {pme && (
        <>
          <div className="grille-2">
            {champ('pc-supp', 'Supplément hors créneau (F)', fiche.offslot_surcharge_fcfa, (v) => maj({ offslot_surcharge_fcfa: v }))}
            <div className="field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: lectureSeule ? 'default' : 'pointer', marginTop: 24 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={optionLD} disabled={lectureSeule} onChange={(e) => setOptionLD(e.target.checked)} />
                Option « longue distance »
              </label>
            </div>
          </div>
          <div className="t-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
            L’option est un abonnement mensuel (facturé en plus du forfait) ; son prix est figé le jour où on la coche. Le supplément et le tarif au km s’appliquent aux courses créées ensuite.
          </div>
        </>
      )}

      <SelecteurAdresse
        titre="Collecte habituelle (épingle)"
        valeur={collecte}
        surChange={(a) => {
          if (lectureSeule) return
          setCollecte(a)
          setCollecteModifiee(true)
        }}
        libelleConnues=""
        placeholderTexte="ex. Boutique Lafia, marché de Lafiabougou"
        placeholderRepere="ex. face à la pharmacie, portail bleu"
      />

      {!lectureSeule && (
        <button className="btn btn-gold btn-sm" style={{ marginTop: 12 }} onClick={() => void enregistrer()} disabled={enCours}>
          <Icone nom="check" /> {enCours ? 'Enregistrement…' : 'Enregistrer la fiche'}
        </button>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- créneaux */

function SectionCreneaux({
  client,
  orgId,
  lectureSeule,
  surRafraichir,
  surNotifier,
}: {
  client: Client
  orgId: string
  lectureSeule: boolean
  surRafraichir: () => Promise<void>
  surNotifier: (m: string) => void
}) {
  const [creneaux, setCreneaux] = useState<Creneau[]>(client.slots)
  const [modifie, setModifie] = useState(false)
  const [enCours, setEnCours] = useState(false)
  const motifs = useMemo(() => controlerCreneaux(creneaux), [creneaux])

  /* Les données reviennent du serveur : tant qu'on n'a rien modifié à la main, l'écran les suit. */
  useEffect(() => {
    if (!modifie) setCreneaux(client.slots)
  }, [client.slots, modifie])

  const maj = (i: number, patch: Partial<Creneau>) => {
    setModifie(true)
    setCreneaux((c) => c.map((s, k) => (k === i ? { ...s, ...patch } : s)))
  }

  async function enregistrer() {
    if (motifs.length) return surNotifier(motifs.join(' '))
    setEnCours(true)
    try {
      await enregistrerCreneaux(orgId, client.id, client.slots, creneaux)
      setModifie(false)
      surNotifier('Créneaux enregistrés')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="panneau-section">
      <h4>
        <Icone nom="clock" classe="h-ic" /> Créneaux de desserte
      </h4>
      <div className="t-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Une course créée hors de ces créneaux (heure de Bamako) porte le supplément hors créneau — calculé par la base, jamais par le navigateur.
      </div>
      {creneaux.length === 0 && <div className="t-muted" style={{ marginBottom: 8 }}>Aucun créneau : le client n’a jamais de supplément hors créneau.</div>}
      {creneaux.map((s, i) => (
        <div className="ligne-creneau" key={s.id ?? `nouveau-${i}`}>
          <ListeDeroulante valeur={String(s.weekday)} options={OPTIONS_JOUR} surChangement={(v) => maj(i, { weekday: parseInt(v, 10) })} pleineLargeur {...(lectureSeule ? { desactive: true } : {})} />
          <input type="time" value={s.start_time} disabled={lectureSeule} onChange={(e) => maj(i, { start_time: e.target.value })} aria-label={`Créneau ${i + 1} : début`} />
          <input type="time" value={s.end_time} disabled={lectureSeule} onChange={(e) => maj(i, { end_time: e.target.value })} aria-label={`Créneau ${i + 1} : fin`} />
          {!lectureSeule && (
            <button
              className="rm"
              type="button"
              title="Retirer le créneau"
              onClick={() => {
                setModifie(true)
                setCreneaux((c) => c.filter((_, k) => k !== i))
              }}
            >
              <Icone nom="x" />
            </button>
          )}
        </div>
      ))}
      {motifs.length > 0 && (
        <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, fontWeight: 600, margin: '4px 0 8px' }}>
          {motifs.join(' ')}
        </p>
      )}
      {!lectureSeule && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => {
              setModifie(true)
              setCreneaux((c) => [...c, { ...CRENEAU_PAR_DEFAUT }])
            }}
          >
            <Icone nom="plus" /> Ajouter un créneau
          </button>
          <button className="btn btn-gold btn-sm" onClick={() => void enregistrer()} disabled={!modifie || enCours || motifs.length > 0}>
            <Icone nom="check" /> {enCours ? 'Enregistrement…' : 'Enregistrer les créneaux'}
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ forfait */

function SectionForfait({
  client,
  donnees,
  surRafraichir,
  surNotifier,
}: {
  client: Client
  donnees: DonneesOps
  surRafraichir: () => Promise<void>
  surNotifier: (m: string) => void
}) {
  const droits = useDroits()
  const peutAbonner = droits.peut('abonnements') && !droits.apercu
  const peutDevis = droits.peut('abonnements_devis')
  const jour = jourBamako()
  const mois = jour.slice(0, 7)
  const forfaits = donnees.forfaits
  const courant = abonnementCourant(donnees.abonnements, client.id)
  const enVigueur = abonnementEnVigueur(donnees.abonnements, client.id, jour)
  const programme = changementProgramme(donnees.abonnements, client.id, jour)
  const historique = historiqueAbonnements(donnees.abonnements, client.id)
  const consommees = coursesDuMois(donnees.livrees, client.id, mois, donnees.billed)

  const actifs = forfaits.filter((f) => f.actif)
  const [plan, setPlan] = useState(actifs[0]?.code ?? '')
  const [prix, setPrix] = useState('')
  const [quota, setQuota] = useState('')
  const [debut, setDebut] = useState(jour)
  const [jourFact, setJourFact] = useState('1')
  const [enCours, setEnCours] = useState(false)
  const [changer, setChanger] = useState(false)

  const forfaitChoisi = forfaits.find((f) => f.code === plan)
  const devis = estSurDevis(forfaitChoisi)
  const interdit = !peutAbonner || client.status !== 'active'

  async function valider(modeChangement: boolean) {
    if (!forfaitChoisi) return surNotifier('Choisissez un forfait.')
    if (devis && !peutDevis) return surNotifier('Un forfait sur devis (prix négocié) est fixé par le propriétaire ou le comptable.')
    let prixNegocie: number | null = null
    let quotaNegocie: number | null = null
    if (devis) {
      prixNegocie = entier(prix)
      if (Number.isNaN(prixNegocie)) return surNotifier('Saisissez le prix mensuel négocié (nombre entier de francs).')
      if (quota.trim() !== '') {
        quotaNegocie = entier(quota)
        if (Number.isNaN(quotaNegocie) || quotaNegocie < 1) return surNotifier('Le quota est un entier d’au moins 1 (vide = illimité).')
      }
    }
    setEnCours(true)
    try {
      if (modeChangement) await changerForfaitClient(client.id, plan, { prix: prixNegocie, quota: quotaNegocie })
      else await abonnerClient(client.id, plan, { prix: prixNegocie, quota: quotaNegocie, debut, jour: Number(jourFact) })
      surNotifier(modeChangement ? `Forfait changé : « ${forfaitChoisi.libelle} » au 1er du mois prochain` : `Abonnement souscrit : ${forfaitChoisi.libelle}`)
      setChanger(false)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  const formulaire = (modeChangement: boolean) => (
    <div style={{ marginTop: 10 }}>
      <div className="field">
        <label>Forfait</label>
        <ListeDeroulante
          valeur={plan}
          options={actifs.map((f) => ({ valeur: f.code, libelle: libelleForfait(f, fcfa) }))}
          surChangement={setPlan}
          pleineLargeur
        />
      </div>
      {devis && (
        <div className="grille-2">
          <div className="field">
            <label htmlFor="pf-prix">Prix mensuel négocié (F TTC)</label>
            <input id="pf-prix" inputMode="numeric" value={prix} disabled={!peutDevis} onChange={(e) => setPrix(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pf-quota">Quota de courses (vide = illimité)</label>
            <input id="pf-quota" inputMode="numeric" value={quota} disabled={!peutDevis} onChange={(e) => setQuota(e.target.value)} />
          </div>
        </div>
      )}
      {devis && !peutDevis && (
        <div className="encart alerte" style={{ marginBottom: 10 }}>
          Un forfait sur devis (prix négocié) est fixé par le propriétaire ou le comptable. Choisissez un forfait de la grille, ou demandez-leur.
        </div>
      )}
      {!modeChangement && (
        <div className="grille-2">
          <div className="field">
            <label htmlFor="pf-debut">Début</label>
            <input id="pf-debut" type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
          </div>
          <div className="field">
            <label>Facturation</label>
            <ListeDeroulante valeur={jourFact} options={OPTIONS_JOUR_FACTURATION} surChangement={setJourFact} pleineLargeur />
          </div>
        </div>
      )}
      <div className="t-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        {modeChangement
          ? 'Le nouveau forfait prend effet le 1er du MOIS SUIVANT, au prix de la grille d’aujourd’hui (figé). Le mois en cours reste facturé à l’ancien forfait.'
          : 'Le prix et le quota de la grille d’aujourd’hui sont FIGÉS : changer la grille plus tard ne touche pas ce client. Le mois de début est facturé en entier (pas de prorata) — poser le début au 1er du mois évite de payer un mois « plein » de trop.'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-gold btn-sm" onClick={() => void valider(modeChangement)} disabled={enCours || !forfaitChoisi || (devis && !peutDevis)}>
          <Icone nom="check" /> {enCours ? 'Enregistrement…' : modeChangement ? 'Changer au 1er du mois prochain' : 'Souscrire'}
        </button>
        {modeChangement && (
          <button className="btn btn-ghost btn-sm" onClick={() => setChanger(false)}>
            Annuler
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div className="panneau-section">
      <h4>
        <Icone nom="receipt" classe="h-ic" /> Forfait et facturation mensuelle
      </h4>
      {!donnees.synchro.abonnements && (
        <div className="encart alerte">Les forfaits et abonnements sont illisibles : la mise à jour de la base (migration 0031) n’est pas appliquée.</div>
      )}
      {donnees.synchro.abonnements && !courant && !enVigueur && (
        <>
          <div className="encart alerte">
            <b>Aucun abonnement.</b> Les courses de ce client ne sont pas facturées par la facturation mensuelle ; elles restent à facturer à la main.
          </div>
          {interdit ? (
            <div className="t-muted" style={{ marginTop: 8 }}>
              {droits.apercu ? 'Aperçu de développement : aucune écriture possible.' : client.status !== 'active' ? 'Le client est en pause ou clos : réactivez-le avant de le souscrire.' : droits.refus}
            </div>
          ) : (
            formulaire(false)
          )}
        </>
      )}
      {(courant || enVigueur) && (
        <>
          {(enVigueur ?? courant) && (
            <div className="encart">
              <div>
                <b>{libelleDuPlan((enVigueur ?? courant)?.plan_code, forfaits)}</b>
                {(enVigueur ?? courant)?.sur_devis && <span className="badge b-gold plain" style={{ marginLeft: 8 }}>sur devis</span>}
                {(enVigueur ?? courant)?.status !== 'active' && (
                  <span className={`badge ${PASTILLE_STATUT_CLIENT[(enVigueur ?? courant)?.status ?? 'active']} plain`} style={{ marginLeft: 8 }}>
                    {LIBELLE_STATUT_CLIENT[(enVigueur ?? courant)?.status ?? 'active']} — jusqu’au {dateCourte((enVigueur ?? courant)?.date_fin)}
                  </span>
                )}
              </div>
              {enVigueur && <div>{descriptionConditions(enVigueur, fcfa)}</div>}
              {enVigueur?.option_longue_distance && <div>Option longue distance : {fcfa(enVigueur.option_prix_mensuel_fcfa)} par mois (prix figé)</div>}
              {enVigueur && <div className="t-muted">Depuis le {dateCourte(enVigueur.date_debut)} · facturé le {enVigueur.jour_facturation} du mois suivant · prix et quota FIGÉS à la souscription</div>}
              {enVigueur && (
                <div style={{ marginTop: 6 }}>
                  Ce mois-ci : <b>{consommees}</b> course{consommees > 1 ? 's' : ''} livrée{consommees > 1 ? 's' : ''}
                  {enVigueur.quota_courses !== null && (
                    <>
                      {' '}
                      sur {enVigueur.quota_courses} incluse{enVigueur.quota_courses > 1 ? 's' : ''}
                      {consommees > enVigueur.quota_courses && <b style={{ color: 'var(--err)' }}> — {consommees - enVigueur.quota_courses} en dépassement</b>}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {programme && (
            <div className="encart alerte" style={{ marginTop: 8 }}>
              <b>Changement programmé</b> au {dateCourte(programme.date_debut)} : {libelleDuPlan(programme.plan_code, forfaits)} — {descriptionConditions(programme, fcfa)}.
            </div>
          )}
          {!programme && !enVigueur && courant && (
            <div className="encart" style={{ marginTop: 8 }}>
              Abonnement programmé à partir du {dateCourte(courant.date_debut)} : {libelleDuPlan(courant.plan_code, forfaits)} — {descriptionConditions(courant, fcfa)}.
            </div>
          )}
          {courant && !interdit && !changer && (
            <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={() => setChanger(true)}>
              Changer de forfait (au 1er du mois prochain)
            </button>
          )}
          {courant && changer && !interdit && formulaire(true)}
          {!courant && enVigueur && (
            <div className="t-muted" style={{ marginTop: 8, fontSize: 12.5 }}>
              L’abonnement se termine le {dateCourte(enVigueur.date_fin)} : réactivez le client pour le reprendre aux mêmes conditions.
            </div>
          )}
        </>
      )}
      {historique.length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>Historique ({historique.length} périodes)</summary>
          <ul className="liste-lignes" style={{ marginTop: 8 }}>
            {historique.map((a) => (
              <li key={a.id}>
                <span>
                  {libelleDuPlan(a.plan_code, forfaits)} · {fcfa(a.prix_mensuel_fcfa)}
                  {a.quota_courses === null ? ' · illimité' : ` · ${a.quota_courses} courses`}
                </span>
                <span className="t-muted">
                  {dateCourte(a.date_debut)} → {a.date_fin ? dateCourte(a.date_fin) : 'en cours'}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ statut */

function SectionStatut({
  client,
  donnees,
  lectureSeule,
  surRafraichir,
  surNotifier,
}: {
  client: Client
  donnees: DonneesOps
  lectureSeule: boolean
  surRafraichir: () => Promise<void>
  surNotifier: (m: string) => void
}) {
  const [enCours, setEnCours] = useState(false)
  const aAbo = client.type === 'pme' && donnees.abonnements.some((a) => a.client_account_id === client.id)

  async function changer(vers: StatutClient) {
    const verbe = vers === 'paused' ? 'mettre en pause' : vers === 'closed' ? 'clôturer' : 'réactiver'
    if (!window.confirm(`Voulez-vous ${verbe} « ${client.name} » ?\n\n${consequenceStatut(vers, aAbo)}`)) return
    setEnCours(true)
    try {
      await modifierClient(client.id, { status: vers })
      surNotifier(`${client.name} : ${vers === 'active' ? 'réactivé' : vers === 'paused' ? 'mis en pause' : 'clôturé'}`)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="panneau-section">
      <h4>
        <Icone nom="shield" classe="h-ic" /> Statut du client
      </h4>
      <div style={{ marginBottom: 8 }}>
        <span className={`badge ${PASTILLE_STATUT_CLIENT[client.status]}`}>{LIBELLE_STATUT_CLIENT[client.status]}</span>
        {client.status === 'closed' && <span className="t-muted" style={{ marginLeft: 8, fontSize: 12.5 }}>Retiré de la prise d’appel ; ses factures et courses restent dans les livres.</span>}
      </div>
      {lectureSeule ? (
        <div className="t-muted">Lecture seule.</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {client.status === 'active' && (
            <button className="btn btn-outline btn-sm" disabled={enCours} onClick={() => void changer('paused')}>
              Mettre en pause
            </button>
          )}
          {client.status !== 'active' && (
            <button className="btn btn-gold btn-sm" disabled={enCours} onClick={() => void changer('active')}>
              Réactiver
            </button>
          )}
          {client.status !== 'closed' && (
            <button className="btn btn-outline btn-sm" style={{ color: 'var(--err)' }} disabled={enCours} onClick={() => void changer('closed')}>
              Clôturer
            </button>
          )}
        </div>
      )}
      <div className="t-muted" style={{ fontSize: 12, marginTop: 8 }}>
        {client.status === 'active' ? consequenceStatut('paused', aAbo) : consequenceStatut('active', aAbo)}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- adresses */

function SectionAdresses({
  client,
  orgId,
  lectureSeule,
  surNotifier,
}: {
  client: Client
  orgId: string
  lectureSeule: boolean
  surNotifier: (m: string) => void
}) {
  const { apercu } = useDroits()
  const [adresses, setAdresses] = useState<AdresseConnue[] | null>(null)
  const [erreur, setErreur] = useState('')

  useEffect(() => {
    if (apercu) {
      setAdresses([])
      return
    }
    let vivant = true
    adressesCompletesDuClient(orgId, client.id)
      .then((a) => vivant && setAdresses(a))
      .catch((e: unknown) => {
        if (vivant) {
          setAdresses([])
          setErreur(e instanceof Error ? e.message : String(e))
        }
      })
    return () => {
      vivant = false
    }
  }, [orgId, client.id, apercu])

  async function supprimer(a: AdresseConnue) {
    if (!window.confirm(`Supprimer l’adresse « ${intituleAdresse(a)} » ?\nLa prochaine course la proposera de nouveau seulement si elle est ressaisie.`)) return
    try {
      await supprimerAdresse(orgId, a.id)
      setAdresses((l) => (l ?? []).filter((x) => x.id !== a.id))
      surNotifier('Adresse supprimée')
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <div className="panneau-section">
      <h4>
        <Icone nom="pin" classe="h-ic" /> Adresses enregistrées
      </h4>
      {adresses === null && <div className="t-muted">Chargement…</div>}
      {erreur && <div className="encart alerte">Adresses illisibles : {erreur}</div>}
      {adresses && adresses.length === 0 && !erreur && (
        <div className="t-muted">Aucune adresse mémorisée : elles s’ajoutent d’elles-mêmes à la prise d’appel (épingle, Plus Code).</div>
      )}
      {adresses && adresses.length > 0 && (
        <ul className="liste-lignes">
          {adresses.map((a) => (
            <li key={a.id}>
              <span>
                <b>{intituleAdresse(a)}</b>
                <span className="t-muted" style={{ display: 'block', fontSize: 11.5 }}>
                  {LIBELLE_SOURCE[a.source]} · utilisée {a.use_count} fois · {a.lat.toFixed(5)} ; {a.lng.toFixed(5)}
                  {a.repere ? ` · ${a.repere}` : ''}
                </span>
              </span>
              {!lectureSeule && (
                <button className="btn btn-outline btn-sm" onClick={() => void supprimer(a)} aria-label={`Supprimer l’adresse ${intituleAdresse(a)}`} title="Supprimer">
                  <Icone nom="x" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
